import { SxProp } from 'dripsy';
import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { TextInput } from 'react-native-gesture-handler';

export enum UniInputVariant {
  v1 = 'V1',
  v2 = 'V2',
  v3 = 'V3',
  v4 = 'V4',
  v5 = 'V5',
  v6 = 'V6'
}

enum StyleName {
  containerV1 = 'containerV1',
  containerV2 = 'containerV2',
  containerV3 = 'containerV3',
  containerV4 = 'containerV4',
  containerV5 = 'containerV5',
  containerV6 = 'containerV6',
  inputV1 = 'inputV1',
  inputV2 = 'inputV2',
  inputV3 = 'inputV3',
  inputV4 = 'inputV4',
  inputV5 = 'inputV5',
  inputV6 = 'inputV6'
}

interface UseInputThemeProps {
  size: 'small' | 'medium' | 'large' | 'default';
  variant: UniInputVariant;
  defaultFocus?: boolean;
  isError?: boolean;
  disabled?: boolean;
}

interface UseInputTheme {
  ref: React.RefObject<TextInput>;
  hasFocus: boolean;
  wrapperStyle: SxProp;
  inputStyle: SxProp;
  onFocus: () => void;
  onBlur: () => void;
  focus: () => void;
  blur: () => void;
}

export const useInputTheme = (props?: UseInputThemeProps): UseInputTheme => {
  const { variant = UniInputVariant.v1, size = 'medium', defaultFocus = false, isError, disabled } = props || {};

  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const textInputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (props?.defaultFocus) {
      setHasFocus(props.defaultFocus);
    }
  }, []);

  function onFocus() {
    setHasFocus(true);
  }

  function onBlur() {
    setHasFocus(false);
  }

  function focus() {
    textInputRef.current?.focus();
    onFocus();
  }

  function blur() {
    textInputRef.current?.blur();
    onBlur();
  }

  const focusHighlightBackground = [UniInputVariant.v4, UniInputVariant.v5, UniInputVariant.v6].includes(variant);

  let wrapperBorderColor = hasFocus ? '$borderInputFocused' : '$borderField';
  if (isError) {
    wrapperBorderColor = '$danger';
  }

  let wrapperBackgroundColor = focusHighlightBackground && hasFocus ? '$accent' : styles[`container${variant}`].backgroundColor;

  if (disabled) {
    wrapperBorderColor = '$disable';
    wrapperBackgroundColor = '$disable';
  }

  const wrapperHeight = {
    small: 28,
    medium: 44,
    large: 56,
    default: 36
  };

  let wrapperStyle = {
    ...baseStyles.wrapper,
    ...styles[`container${variant}`],
    height: wrapperHeight[size],
    borderColor: wrapperBorderColor,
    backgroundColor: wrapperBackgroundColor
  };

  if (variant === UniInputVariant.v3 && size !== 'small') {
    wrapperStyle = {
      ...wrapperStyle,
      alignItems: 'flex-end',
      pb: 8
    };
  }

  const inputStyle = {
    ...styles[`input${variant}`],
    color: disabled ? '$textDisable' : '$textPrimary'
  };

  return {
    ref: textInputRef,
    hasFocus,
    wrapperStyle,
    inputStyle,
    onFocus,
    onBlur,
    focus,
    blur
  };
};

const baseStyles: Record<string, SxProp> = {
  baseWrapperSpacing: {},
  baseInputSpacing: {
    flex: 1,
    height: 'auto',
    paddingX: 16,
    outlineStyle: 'none'
  },
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: Platform.OS === 'web' ? 'wrap' : 'nowrap'
  }
};

const styles: Record<StyleName, SxProp> = {
  containerV1: {
    ...baseStyles.baseWrapperSpacing,
    borderWidth: 1,
    borderRadius: 5,
    borderColor: '$borderButton'
  },
  containerV2: {
    ...baseStyles.baseWrapperSpacing,
    borderWidth: 2
  },
  containerV3: {
    ...baseStyles.baseWrapperSpacing,
    borderBottomWidth: 1.5
  },
  containerV4: {
    ...baseStyles.baseWrapperSpacing,
    borderWidth: 2,
    backgroundColor: '$accent'
  },
  containerV5: {
    ...baseStyles.baseWrapperSpacing,
    backgroundColor: '#FAFAFA',
    borderWidth: 1,
    borderColor: '$borderButton'
  },
  containerV6: {
    ...baseStyles.baseWrapperSpacing,
    borderWidth: 1.5,
    borderBottomWidth: 4,
    borderRadius: 5,
    borderColor: '$borderButton'
  },
  inputV1: {
    ...baseStyles.baseInputSpacing
  },
  inputV2: {
    ...baseStyles.baseInputSpacing
  },
  inputV3: {
    ...baseStyles.baseInputSpacing,
    paddingX: 12
  },
  inputV4: {
    ...baseStyles.baseInputSpacing
  },
  inputV5: {
    ...baseStyles.baseInputSpacing
  },
  inputV6: {
    ...baseStyles.baseInputSpacing
  }
};
